import { useEffect, useState } from 'react';
import { X, ArrowLeft, HeartHandshake } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import TermsAndConditions from './TermsAndConditions';
import PrivacyPolicy from './PrivacyPolicy';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceType: 'private';
  prefilledCode?: string;
}

type BookingView = 'choice' | 'payment' | 'sponsorCode' | 'requestSponsored';

const BookingModal = ({ isOpen, onClose, serviceType, prefilledCode = '' }: BookingModalProps) => {
  const { t, i18n } = useTranslation();
  const defaultCalUrl = 'https://cal.com/careandtherapy.com/50min';
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const isEnglishContext =
    hostname.includes('careandtherapy.com') ||
    (!hostname.includes('omsorgsterapi.dk') && i18n.resolvedLanguage?.startsWith('en'));
  const supportLinkEn = import.meta.env.VITE_STRIPE_SUPPORT_LINK_EN?.trim() || '';
  const supportLinkDa = import.meta.env.VITE_STRIPE_SUPPORT_LINK_DA?.trim() || '';
  const supportLink = isEnglishContext
    ? supportLinkEn || supportLinkDa
    : supportLinkDa || supportLinkEn;
  const [currentView, setCurrentView] = useState<BookingView>('choice');
  const [sponsorCode, setSponsorCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [calUrl, setCalUrl] = useState(defaultCalUrl);
  const [requestEmail, setRequestEmail] = useState('');
  const [requestConsentAccepted, setRequestConsentAccepted] = useState(false);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSuccess, setRequestSuccess] = useState('');
  const getEmbedUrl = (url: string) => `${url}${url.includes('?') ? '&' : '?'}embed=true&theme=light`;

  // Pre-fill sponsor code if provided via URL and go directly to sponsor code view
  useEffect(() => {
    if (prefilledCode && isOpen) {
      setSponsorCode(prefilledCode);
      setCurrentView('sponsorCode');
    }
  }, [prefilledCode, isOpen]);

  // Reset to choice view when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentView('choice');
      setSponsorCode('');
      setError('');
      setCalUrl(defaultCalUrl);
      setRequestEmail('');
      setRequestConsentAccepted(false);
      setIsSubmittingRequest(false);
      setRequestError('');
      setRequestSuccess('');
    }
  }, [isOpen]);

  // Helper function to switch to payment view with default URL
  const goToPaymentView = () => {
    setCalUrl(defaultCalUrl);
    setCurrentView('payment');
  };

  // Helper function to go back to choice view
  const goToChoiceView = () => {
    setCalUrl(defaultCalUrl);
    setCurrentView('choice');
    setRequestError('');
    setRequestSuccess('');
  };

  const goToRequestSponsoredView = () => {
    setRequestError('');
    setRequestSuccess('');
    setCurrentView('requestSponsored');
  };

  const openSupportLink = () => {
    if (!supportLink) return;

    window.open(supportLink, '_blank', 'noopener,noreferrer');
  };

  const handleValidateCode = async () => {
    if (!sponsorCode.trim()) {
      setError(t('booking.codeRequired'));
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/validate-code'
        : 'http://localhost:3000/api/validate-code';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: sponsorCode }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Set the private Cal.com link and switch to payment view to show in iframe
        setCalUrl(data.link);
        setCurrentView('payment');
      } else {
        // Handle errors
        if (response.status === 404) {
          setError(t('booking.invalidCode'));
        } else if (response.status === 410) {
          setError(t('booking.codeUsed'));
        } else {
          setError(data.error || t('booking.errorValidating'));
        }
      }
    } catch (err) {
      console.error('Error validating code:', err);
      setError(t('booking.errorValidating'));
    } finally {
      setIsValidating(false);
    }
  };

  const handleRequestSponsoredCode = async () => {
    const normalizedEmail = requestEmail.trim().toLowerCase();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!normalizedEmail) {
      setRequestError(t('booking.requestEmailRequired'));
      return;
    }

    if (!emailPattern.test(normalizedEmail)) {
      setRequestError(t('booking.requestEmailInvalid'));
      return;
    }

    if (!requestConsentAccepted) {
      setRequestError(t('booking.requestConsentRequired'));
      return;
    }

    setIsSubmittingRequest(true);
    setRequestError('');

    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/request-sponsored-code'
        : 'http://localhost:3000/api/request-sponsored-code';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: normalizedEmail,
          language: i18n.resolvedLanguage?.startsWith('da') ? 'da' : 'en',
          acceptedTerms: true,
          acceptedPrivacy: true,
          acceptedEmailStorage: true,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setRequestError(data.error || t('booking.requestError'));
        return;
      }

      setRequestSuccess(t('booking.requestSuccess'));
    } catch (err) {
      console.error('Error requesting sponsored code:', err);
      setRequestError(t('booking.requestError'));
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Load Cal.com embed script
      const script = document.createElement('script');
      script.src = 'https://app.cal.com/embed/embed.js';
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Render choice view
  const renderChoiceView = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl p-8">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-[#006B5F] mb-2">
            📅 {t('booking.title')}
          </h2>
          <p className="text-gray-600">
            {t('booking.howToBook')}
          </p>
        </div>

        <div className="space-y-4">
          {/* Payment Option */}
          <button
            onClick={goToPaymentView}
            className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-[#006B5F] hover:bg-gray-50 transition-all text-left group"
          >
            <div className="flex items-start gap-4">
              <div className="text-3xl">💳</div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#006B5F] transition-colors">
                  {t('booking.wantToPay')}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t('booking.sessionPrice')}
                </p>
              </div>
            </div>
          </button>

          {/* Sponsor Code Option */}
          <button
            onClick={() => setCurrentView('sponsorCode')}
            className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-[#006B5F] hover:bg-gray-50 transition-all text-left group"
          >
            <div className="flex items-start gap-4">
              <div className="text-3xl">🎁</div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#006B5F] transition-colors">
                  {t('booking.haveSponsorCode')}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t('booking.freeSession')}
                </p>
              </div>
            </div>
          </button>

        </div>

        <div className="mt-5 rounded-lg border border-dashed border-gray-300 bg-gray-50/80 p-4">
          <button
            onClick={goToRequestSponsoredView}
            className="text-sm font-medium text-gray-700 underline decoration-gray-400 underline-offset-4 transition-colors hover:text-[#006B5F]"
          >
            {t('booking.requestSponsoredButton')}
          </button>
          <p className="mt-2 text-sm text-gray-600">
            {t('booking.requestSponsoredHint')}
          </p>
        </div>

        {supportLink && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={openSupportLink}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-[#006B5F] hover:text-[#006B5F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#006B5F] focus-visible:ring-offset-2"
            >
              <HeartHandshake className="h-4 w-4" />
              {t('booking.supportWork')}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Render payment view (Cal.com iframe)
  const renderPaymentView = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative w-full max-w-4xl h-[90vh] bg-white rounded-lg shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <button
            onClick={goToChoiceView}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">{t('booking.back')}</span>
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 p-4 overflow-hidden">
          <iframe
            src={getEmbedUrl(calUrl)}
            className="w-full h-full rounded-lg border-0"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );

  // Render sponsor code view
  const renderSponsorCodeView = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={goToChoiceView}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">{t('booking.back')}</span>
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-xl font-bold text-[#006B5F] mb-2">
            🎁 {t('booking.sponsoredCallTitle')}
          </h3>
          <p className="text-gray-600 text-sm">
            {t('booking.sponsoredCallDescription')}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="sponsorCode" className="block text-sm font-medium text-gray-700 mb-2">
              {t('booking.enterCode')}
            </label>
            <input
              type="text"
              id="sponsorCode"
              value={sponsorCode}
              onChange={(e) => setSponsorCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleValidateCode()}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006B5F] focus:border-transparent"
              placeholder={t('booking.codePlaceholder')}
              disabled={isValidating}
              autoFocus
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}

          <button
            onClick={handleValidateCode}
            disabled={isValidating}
            className="w-full bg-[#006B5F] hover:bg-[#005A4F] text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isValidating ? t('booking.validating') : t('booking.validateCode')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderRequestSponsoredView = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={goToChoiceView}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">{t('booking.back')}</span>
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {requestSuccess ? (
          <div className="space-y-4">
            <div className="mb-2">
              <h3 className="text-xl font-bold text-[#006B5F]">
                {t('booking.requestSuccessTitle')}
              </h3>
            </div>
            <div className="rounded-lg bg-green-50 p-4 text-sm text-green-800">
              {requestSuccess}
            </div>
            <button
              onClick={onClose}
              className="w-full bg-[#006B5F] hover:bg-[#005A4F] text-white py-2 px-4 rounded-lg transition-colors"
            >
              {t('booking.requestClose')}
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-[#006B5F] mb-2">
                {t('booking.requestSponsoredTitle')}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t('booking.requestSponsoredDescription')}
              </p>
            </div>
            <div>
              <label htmlFor="requestEmail" className="block text-sm font-medium text-gray-700 mb-2">
                {t('booking.requestEmailLabel')}
              </label>
              <input
                type="email"
                id="requestEmail"
                value={requestEmail}
                onChange={(e) => setRequestEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRequestSponsoredCode()}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006B5F] focus:border-transparent"
                placeholder={t('booking.requestEmailPlaceholder')}
                disabled={isSubmittingRequest}
                autoFocus
              />
            </div>

            <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="request-consent"
                  checked={requestConsentAccepted}
                  onCheckedChange={(checked) => setRequestConsentAccepted(checked === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="request-consent" className="text-sm font-normal leading-relaxed text-gray-700 cursor-pointer">
                  {t('booking.requestConsent')}
                </Label>
              </div>

              <div className="border-t border-gray-200 pt-4 text-xs text-gray-600">
                <span className="mr-2">{t('booking.requestConsentHelp')}</span>
                <TermsAndConditions triggerLabel={t('booking.requestTermsLink')} className="mr-3 text-xs" />
                <PrivacyPolicy triggerLabel={t('booking.requestPrivacyLink')} className="text-xs text-[#006B5F] hover:text-[#004a3f] font-semibold underline" />
              </div>
            </div>

            {requestError && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                {requestError}
              </div>
            )}

            <button
              onClick={handleRequestSponsoredCode}
              disabled={isSubmittingRequest}
              className="w-full bg-[#006B5F] hover:bg-[#005A4F] text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmittingRequest ? t('booking.requestSubmitting') : t('booking.requestSubmit')}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {currentView === 'choice' && renderChoiceView()}
      {currentView === 'payment' && renderPaymentView()}
      {currentView === 'sponsorCode' && renderSponsorCodeView()}
      {currentView === 'requestSponsored' && renderRequestSponsoredView()}
    </>
  );
};

export default BookingModal;
